import type { JSX } from 'solid-js'
import { createEffect, createMemo, createSignal, createUniqueId, Index, mergeProps, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import { useFieldError } from '~/hooks/useFieldError'
import type { TagInputPropsType } from '~/types/TagInput'
import { controlSize } from '~/utils/controlSize'
import Chip from './Chip'
import FieldMessage from './FieldMessage'
import { useFormContext } from './Form'

export default function TagInput(props: TagInputPropsType): JSX.Element {
  const merged = mergeProps(
    {
      color: 'primary' as const,
      size: 'md' as const,
      placeholder: 'Agregar tag...',
      disabled: false,
      readOnly: false,
      maxTags: Infinity,
      minTags: 0,
      allowDuplicates: false,
      tags: undefined as string[] | undefined,
      outlined: false,
      mode: 'input',
      availableTags: [] as string[],
      filterAvailableTags: true,
      noOptionsMessage: 'No hay opciones disponibles',
      separator: ',',
      valueFormat: 'array' as const,
      variant: 'default' as const
    },
    props
  )

  const [local, inputAttrs] = splitProps(merged, [
    'color',
    'size',
    'placeholder',
    'disabled',
    'readOnly',
    'maxTags',
    'minTags',
    'allowDuplicates',
    'validateTag',
    'tagClassName',
    'tags',
    'onTagsChange',
    'outlined',
    'label',
    'error',
    'helperText',
    'class',
    'classList',
    'mode',
    'availableTags',
    'filterAvailableTags',
    'noOptionsMessage',
    'name',
    'separator',
    'valueFormat',
    'variant',
    'ref'
  ])

  // name se maneja con hidden input — no pasar al input visible
  // 'name' ya fue extraído en local por el splitProps anterior

  const formCtx = useFormContext()

  // Estado interno de tags — fuente de verdad
  const [tags, setTagsInternal] = createSignal<string[]>([])
  const [inputValue, setInputValue] = createSignal('')
  const [isOpen, setIsOpen] = createSignal(false)
  const [selectedIdx, setSelectedIdx] = createSignal(-1)
  const [dropStyle, setDropStyle] = createSignal({ top: 0, left: 0, width: 0 })
  const [hasFocus, setHasFocus] = createSignal(false)

  const id = createUniqueId()
  let triggerRef: HTMLDivElement | undefined
  let menuRef: HTMLDivElement | undefined
  let inputRef: HTMLInputElement | undefined
  let positionTimer: ReturnType<typeof setTimeout> | undefined

  // Inicializar tags: prop `tags` tiene prioridad, luego initialValues del Form
  const getInitialTags = (): string[] => {
    if (local.tags && local.tags.length > 0) return local.tags.map(String)
    if (local.name && formCtx) {
      const iv = formCtx.initialValues[local.name]
      if (Array.isArray(iv) && iv.length > 0) return iv.map(String)
      if (typeof iv === 'string' && iv) {
        if (local.valueFormat === 'string' && local.separator) {
          return iv
            .split(local.separator)
            .map(s => s.trim())
            .filter(Boolean)
        }
        try {
          const p = JSON.parse(iv)
          if (Array.isArray(p)) return p.map(String)
        } catch {
          /* noop */
        }
        return [iv]
      }
    }
    return []
  }

  // Inicializar signal con el valor correcto desde el principio
  const [initialized] = createSignal(getInitialTags())
  if (initialized().length > 0) setTagsInternal(initialized())

  // Sincronizar cuando la prop `tags` cambia desde afuera (modo controlado)
  createEffect(() => {
    const external = local.tags
    if (external !== undefined) {
      const next = external.map(String)
      const curr = tags()
      if (JSON.stringify(next) !== JSON.stringify(curr)) {
        setTagsInternal(next)
      }
    }
  })

  // Notificar cambios al Form y al callback externo
  const commitTags = (next: string[]) => {
    setTagsInternal(next)
    local.onTagsChange?.(next)
  }

  // ── Lógica de tags ─────────────────────────────────────────────────────────

  const addTag = (raw: string) => {
    const sep = local.separator
    // Si contiene separador, agregar múltiples
    if (sep && raw.includes(sep)) {
      const parts = raw
        .split(sep)
        .map(t => t.trim())
        .filter(Boolean)
      const current = tags()
      const next = [...current]
      let changed = false
      for (const part of parts) {
        if (local.validateTag && !local.validateTag(part)) continue
        if (!local.allowDuplicates && next.includes(part)) continue
        if (next.length >= local.maxTags) break
        next.push(part)
        changed = true
      }
      if (changed) commitTags(next)
      clearInput()
      return
    }

    const trimmed = raw.trim().replace(new RegExp(`${sep}$`), '')
    if (!trimmed) return
    if (local.validateTag && !local.validateTag(trimmed)) return
    if (!local.allowDuplicates && tags().includes(trimmed)) return
    if (tags().length >= local.maxTags) return

    commitTags([...tags(), trimmed])
    clearInput()
  }

  const removeTag = (idx: number) => {
    if (tags().length <= local.minTags) return
    const next = tags().filter((_, i) => i !== idx)
    commitTags(next)
  }

  const clearInput = () => {
    setInputValue('')
    if (inputRef) inputRef.value = ''
    setSelectedIdx(-1)
    setIsOpen(false)
  }

  // ── Filtrado para modo select ───────────────────────────────────────────────

  const filteredOptions = createMemo(() => {
    if (local.mode !== 'select') return []
    let opts = local.availableTags
    if (local.filterAvailableTags && inputValue()) {
      const q = inputValue().toLowerCase()
      opts = opts.filter(t => t.toLowerCase().includes(q))
    }
    if (!local.allowDuplicates) {
      const set = new Set(tags())
      opts = opts.filter(t => !set.has(t))
    }
    return opts
  })

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleInput = (e: Event) => {
    const val = (e.target as HTMLInputElement).value
    if (local.separator && val.includes(local.separator)) {
      addTag(val)
    } else {
      setInputValue(val)
      if (local.mode === 'select' && (e as InputEvent).isTrusted) {
        setIsOpen(true)
        setSelectedIdx(-1)
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    const opts = filteredOptions()
    if (e.key === 'Enter') {
      e.preventDefault()
      if (local.mode === 'select' && selectedIdx() >= 0 && opts[selectedIdx()]) {
        addTag(opts[selectedIdx()])
      } else if (inputValue()) {
        addTag(inputValue())
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setSelectedIdx(-1)
    } else if (e.key === 'Backspace' && !inputValue() && tags().length > 0) {
      removeTag(tags().length - 1)
    } else if (local.mode === 'select' && isOpen()) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx(i => Math.min(i + 1, opts.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx(i => Math.max(i - 1, -1))
      }
    } else if (e.key === 'ArrowDown' && local.mode === 'select' && !isOpen()) {
      setIsOpen(true)
      setSelectedIdx(0)
    }
  }

  const handleBlur = () => {
    setHasFocus(false)
    setTimeout(() => {
      if (inputValue() && !isOpen()) addTag(inputValue())
    }, 150)
  }

  const handleClickOutside = (e: MouseEvent) => {
    const t = e.target as Node
    if (triggerRef && !triggerRef.contains(t) && menuRef && !menuRef.contains(t)) {
      setIsOpen(false)
    }
  }

  // ── Posición del dropdown ──────────────────────────────────────────────────

  const updatePosition = () => {
    if (positionTimer) clearTimeout(positionTimer)
    positionTimer = setTimeout(() => {
      if (!triggerRef) return
      const r = triggerRef.getBoundingClientRect()
      const menuH = 200
      const below = window.innerHeight - r.bottom
      const above = r.top
      const showAbove = below < menuH && above > below
      setDropStyle({
        top: showAbove ? r.top - menuH : r.bottom,
        left: r.left,
        width: r.width
      })
    }, 16)
  }

  createEffect(() => {
    if (isOpen()) {
      document.addEventListener('mousedown', handleClickOutside)
      updatePosition()
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  })

  // ── Único onMount: registrar en Form + listeners de ventana ───────────────

  onMount(() => {
    // Registrar en el Form vía Context
    if (local.name && formCtx) {
      // Si initialValues tiene un valor para este campo, aplicarlo ahora
      // (puede que getInitialTags() ya lo haya tomado, pero por si acaso)
      const iv = formCtx.initialValues[local.name]
      if (iv !== undefined && tags().length === 0) {
        let arr: string[] = []
        if (Array.isArray(iv)) {
          arr = iv.map(String)
        } else if (typeof iv === 'string' && iv) {
          if (local.valueFormat === 'string' && local.separator) {
            arr = iv
              .split(local.separator)
              .map(s => s.trim())
              .filter(Boolean)
          } else {
            try {
              const p = JSON.parse(iv)
              if (Array.isArray(p)) arr = p.map(String)
            } catch {
              arr = [iv]
            }
          }
        }
        if (arr.length > 0) commitTags(arr)
      }

      formCtx.registerField(local.name, {
        getValue: () => tags(),
        setValue: value => {
          if (value === undefined || value === null) return commitTags([])
          if (Array.isArray(value)) return commitTags(value.map(String))
          if (typeof value !== 'string') return commitTags([String(value)])
          try {
            const parsed: unknown = JSON.parse(value)
            commitTags(Array.isArray(parsed) ? parsed.map(String) : [value])
          } catch {
            commitTags([value])
          }
        }
      })
    }

    // Listeners de resize/scroll para el dropdown
    const onResize = () => {
      if (isOpen()) updatePosition()
    }
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)

    onCleanup(() => {
      if (local.name && formCtx) formCtx.unregisterField(local.name)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
      document.removeEventListener('mousedown', handleClickOutside)
      if (positionTimer) clearTimeout(positionTimer)
    })
  })

  // ── Clases ─────────────────────────────────────────────────────────────────

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto.
  const ownError = () => {
    if (local.error) return local.error
    // El mínimo de etiquetas es un error del campo como cualquier otro. Iba en
    // un aviso aparte, así que podían salir dos mensajes apilados a la vez.
    if (local.minTags > 0 && tags().length < local.minTags) return `Mínimo ${local.minTags} ${local.minTags === 1 ? 'etiqueta' : 'etiquetas'}`
    return undefined
  }
  const resolvedError = useFieldError(() => (local.name ? String(local.name) : undefined), ownError)

  const hasError = () => Boolean(resolvedError())

  const variantClasses = createMemo(() =>
    twMerge(
      'focus-within:outline-none focus-within:ring-0',
      'focus-within:border-primary',
      local.variant === 'text' ? 'border-b-2' : '',
      local.variant === 'outlined' ? 'border-2' : '',
      ['text', 'outlined'].includes(local.variant ?? 'default') && hasError() ? 'border-danger' : 'border-border-strong',
      local.variant === 'default' ? 'border bg-surface-muted' : '',
      local.variant === 'default' && hasError() ? 'border border-danger' : '',
      hasError() ? 'focus-within:border-danger' : ''
    )
  )

  const sizeConfig = createMemo(() => controlSize(local.size))
  /** Talla de los chips, que no tiene equivalente en la escala común. */
  const chipSize = createMemo(() => (({ xs: 'xs', sm: 'sm', md: 'sm', lg: 'md', xl: 'lg' }) as const)[local.size ?? 'md'] ?? 'sm')

  /**
   * Crece con los chips, así que la escala marca el arranque y no la altura
   * final: es el mismo trato que el Select en modo múltiple.
   */
  const wrapClass = createMemo(() =>
    twMerge(
      'relative w-full rounded-lg duration-100 transition-all cursor-text flex items-center',
      variantClasses(),
      sizeConfig().minHeight,
      sizeConfig().padX,
      local.label ? sizeConfig().labelPad : 'py-0',
      local.disabled ? 'opacity-70 cursor-not-allowed' : '',
      local.class
    )
  )

  /** Flota en cuanto hay chips o foco; en reposo va centrada sobre la línea. */
  const labelFloated = createMemo(() => tags().length > 0 || hasFocus())

  const labelClass = createMemo(() =>
    twMerge(
      'absolute block select-none whitespace-nowrap overflow-hidden text-ellipsis',
      'max-w-[calc(100%-1rem)] duration-100 transition-all z-10 origin-left',
      sizeConfig().padStart,
      labelFloated() ? `${sizeConfig().labelFloating} top-1 translate-y-0` : `${sizeConfig().text} top-1/2 -translate-y-1/2`,
      hasError() ? 'text-danger' : 'text-foreground-subtle'
    )
  )

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div class="relative w-full" classList={local.classList}>
      <div ref={triggerRef} class="relative">
        {/* Contenedor principal */}
        <div
          class={wrapClass()}
          onMouseDown={e => {
            if (!local.disabled && !local.readOnly && inputRef) {
              const t = e.target as HTMLElement
              if (t === e.currentTarget || t.classList.contains('flex-wrap')) {
                e.preventDefault()
                inputRef.focus()
              }
            }
          }}
        >
          <div class="flex flex-wrap gap-1.5 items-center w-full min-h-6">
            <Index each={tags()}>
              {(tag, idx) => (
                <Chip
                  size={chipSize()}
                  color={local.color}
                  variant="soft"
                  rounded
                  removable={!local.disabled && !local.readOnly}
                  onRemove={() => removeTag(idx)}
                  class={twMerge(local.tagClassName ?? '', 'my-0.5')}
                >
                  {tag()}
                </Chip>
              )}
            </Index>

            <Show when={!local.readOnly && tags().length < local.maxTags}>
              <input
                {...inputAttrs}
                ref={el => {
                  inputRef = el
                  if (typeof local.ref === 'function') local.ref(el)
                }}
                type="text"
                id={id}
                value={inputValue()}
                placeholder=" "
                disabled={local.disabled}
                class="grow min-w-16 h-6 outline-none bg-transparent text-sm text-foreground peer appearance-none"
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onFocus={e => {
                  setHasFocus(true)
                  if (local.mode === 'select' && (e as FocusEvent & { isTrusted: boolean }).isTrusted) {
                    setIsOpen(true)
                    setSelectedIdx(-1)
                  }
                }}
                onBlur={handleBlur}
              />
            </Show>
          </div>
        </div>

        {/* Hidden input: permite que el Form nativo lea el valor también */}
        <Show when={local.name}>
          <input type="hidden" name={local.name} data-tag-input value={local.valueFormat === 'string' ? tags().join(local.separator ?? ',') : JSON.stringify(tags())} />
        </Show>

        {/* Label flotante */}
        <Show when={local.label}>
          <label for={id} class={labelClass()}>
            {local.label}
          </label>
        </Show>

        {/* Dropdown (solo modo select) */}
        <Show when={isOpen() && local.mode === 'select'}>
          <Portal>
            <div
              ref={menuRef}
              class="fixed z-50 bg-surface border border-border rounded-md shadow-lg max-h-60 overflow-auto"
              style={{ top: `${dropStyle().top}px`, left: `${dropStyle().left}px`, width: `${dropStyle().width}px` }}
            >
              <Show when={filteredOptions().length > 0} fallback={<div class="px-4 py-2 text-foreground-subtle text-sm">{local.noOptionsMessage}</div>}>
                <Index each={filteredOptions()}>
                  {(opt, idx) => (
                    <div
                      class={twMerge('px-4 py-2 cursor-pointer text-sm transition-colors', 'hover:bg-surface-hover', idx === selectedIdx() ? 'bg-surface-muted' : '')}
                      onMouseDown={e => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        addTag(opt())
                      }}
                      onMouseEnter={() => setSelectedIdx(idx)}
                    >
                      {opt()}
                    </div>
                  )}
                </Index>
              </Show>
            </div>
          </Portal>
        </Show>
      </div>

      <FieldMessage size={local.size} error={resolvedError()} helperText={local.helperText} />
    </div>
  )
}
